/**
 * useSubmissionBadges - Achievement badge detection around report submission
 *
 * Extracted from useReportSubmission.js. Owns:
 * - Snapshot of the badge count taken just before a submission
 * - Delayed post-submission re-check (with retries while the Lambda processes)
 * - Earned-badge modal state (respecting the quiet-mode preference)
 *
 * Badge failures are always silent - badges are optional.
 */
import { useState, useRef, useCallback } from 'react';

import Util from '../util/util';
import { getQuietMode } from '../util/storage';

// Delay before checking for badges (allows Lambda to process)
const BADGE_CHECK_DELAY_MS = 2000;
// Retry configuration for badge checks
const BADGE_CHECK_RETRY_DELAY_MS = 1500;
const BADGE_CHECK_MAX_RETRIES = 2;

export default function useSubmissionBadges() {
  const [earnedBadge, setEarnedBadge] = useState(null);
  const [showBadgeModal, setShowBadgeModal] = useState(false);
  const preBadgeCountRef = useRef(0);

  /**
   * Store current badge count before submission
   */
  const storeBadgeCount = useCallback(async () => {
    try {
      const data = await Util.get('Resty', '/items/achievements');
      preBadgeCountRef.current = data?.badges?.length || 0;
    } catch (err) {
      // Silently fail - badges are optional
      preBadgeCountRef.current = 0;
    }
  }, []);

  /**
   * Check for new badges after submission with retry logic
   */
  const checkForNewBadge = useCallback(async (retryCount = 0) => {
    try {
      const data = await Util.get('Resty', '/items/achievements');
      const currentBadgeCount = data?.badges?.length || 0;

      if (currentBadgeCount > preBadgeCountRef.current && data.badges?.length > 0) {
        // User earned a new badge - get the most recent one
        const newBadge = data.badges[data.badges.length - 1];
        setEarnedBadge(newBadge);

        // Check quiet mode preference before showing modal
        if (!(await getQuietMode())) {
          setShowBadgeModal(true);
        }
      } else if (retryCount < BADGE_CHECK_MAX_RETRIES) {
        // Badge not ready yet, retry after delay
        setTimeout(() => {
          checkForNewBadge(retryCount + 1);
        }, BADGE_CHECK_RETRY_DELAY_MS);
      }
    } catch (err) {
      // Silently fail - badges are optional
      console.log('Badge check failed:', err);
    }
  }, []);

  /**
   * Schedule the post-submission badge check (allows Lambda to process)
   */
  const scheduleBadgeCheck = useCallback(() => {
    setTimeout(() => {
      checkForNewBadge();
    }, BADGE_CHECK_DELAY_MS);
  }, [checkForNewBadge]);

  /**
   * Dismiss badge modal
   */
  const dismissBadgeModal = useCallback(() => {
    setShowBadgeModal(false);
    setEarnedBadge(null);
  }, []);

  return {
    earnedBadge,
    showBadgeModal,
    storeBadgeCount,
    checkForNewBadge,
    scheduleBadgeCheck,
    dismissBadgeModal,
  };
}

export { BADGE_CHECK_DELAY_MS, BADGE_CHECK_RETRY_DELAY_MS, BADGE_CHECK_MAX_RETRIES };
